/**
 * pot.ts —— 底池 / 边池的构建与结算（PRD 1.1「底池与边池」、1.2 零头口径、AC1.3 / AC1.4）
 *
 * 为什么单独成模块：
 *   边池与零头分配是本类牌局最容易写错、也最容易被"差 1 个筹码"悄悄破坏守恒的地方。
 *   把它做成**纯函数**（输入本手全部投入 + 弃牌状态 + 牌力，输出每人收多少），
 *   就能脱离状态机独立做穷举式单测，并强制「结算总额 === 投入总额」这条硬断言。
 *
 * 核心口径（PRD 1.2）：
 *   1. 边池分层：按各玩家「本手总投入额」的不同水位切层。弃牌玩家的筹码留在池中
 *      （死钱），但不具备任何池的比牌资格。
 *   2. 结算顺序：主池优先，然后按形成顺序依次结算边池。
 *   3. 零头：池金额不能被赢家人数整除时，余数按顺时针方向、从「庄家按钮左侧第一个
 *      赢家」开始每人多得 1 个筹码；多个池各自独立处理。
 */

/** 一名玩家在本手中的投入与资格信息。 */
export interface PotContribution {
  /** 座位号（0-based），同时用于零头分配的顺时针顺序。 */
  readonly seat: number;
  /** 本手累计投入筹码总额（含盲注与前注）。 */
  readonly contributed: number;
  /** 是否已弃牌。弃牌者的钱留在池里但不参与任何池的比牌。 */
  readonly folded: boolean;
}

/** 一个池（主池或边池）。 */
export interface Pot {
  /** 0 = 主池，1.. = 按形成顺序编号的边池。 */
  readonly index: number;
  /** 池内筹码总额。 */
  readonly amount: number;
  /** 有资格参与该池比牌的座位号（升序）。 */
  readonly eligible: readonly number[];
  /**
   * 该层的投入水位上限。
   * 例：某玩家全下 200，则第一层水位为 200，每个投入 ≥200 的玩家为该层贡献 200。
   */
  readonly cap: number;
}

/** 单个池的结算明细。 */
export interface PotAward {
  readonly potIndex: number;
  readonly amount: number;
  /** 该池的赢家座位（可能多人平分）。 */
  readonly winners: readonly number[];
  /** 每个赢家在该池中实得（已含零头分配）。 */
  readonly payouts: ReadonlyMap<number, number>;
  /** 该池零头筹码数 = amount % winners.length。 */
  readonly remainder: number;
  /** 拿到零头的座位（按顺时针顺序，长度 === remainder）。 */
  readonly remainderSeats: readonly number[];
}

/** 完整结算结果。 */
export interface Settlement {
  readonly pots: readonly Pot[];
  readonly awards: readonly PotAward[];
  /** 每个座位本手总收入（未扣除投入，是"从池里拿回多少"）。 */
  readonly payouts: ReadonlyMap<number, number>;
  /** 本手所有玩家投入总额。 */
  readonly totalContributed: number;
  /** 所有池派发出去的总额，必须精确等于 totalContributed。 */
  readonly totalPaid: number;
}

/**
 * 把本手全部投入切分为主池 + 若干边池。
 *
 * 算法：取所有**有投入**玩家的投入额去重升序作为水位线；
 * 相邻水位之间构成一层，该层金额 = Σ min(投入, 上水位) − min(投入, 下水位)。
 * 该层的比牌资格 = 未弃牌且投入额 ≥ 上水位 的玩家。
 *
 * 这样切分天然处理了三种棘手情况：
 *   - 弃牌者的死钱：仍计入金额，但不进 eligible。
 *   - 未被跟注的下注：最高一层只有 1 名 eligible，等价于把多下的钱退还本人。
 *   - 多人不同额度全下：自动形成多层边池。
 */
export function buildPots(contributions: readonly PotContribution[]): Pot[] {
  for (const c of contributions) {
    if (!Number.isInteger(c.contributed) || c.contributed < 0) {
      throw new Error(`座位 ${c.seat} 的投入额非法：${c.contributed}`);
    }
  }

  const levels = [...new Set(contributions.filter((c) => c.contributed > 0).map((c) => c.contributed))].sort(
    (a, b) => a - b,
  );

  const pots: Pot[] = [];
  let prev = 0;
  for (const level of levels) {
    let amount = 0;
    for (const c of contributions) {
      // 该玩家在 (prev, level] 这一层里出了多少
      amount += Math.max(0, Math.min(c.contributed, level) - prev);
    }
    const eligible = contributions
      .filter((c) => !c.folded && c.contributed >= level)
      .map((c) => c.seat)
      .sort((a, b) => a - b);

    if (amount > 0) {
      // 合并「资格名单完全相同」的相邻层，避免产生一堆语义重复的边池
      const last = pots[pots.length - 1];
      if (last && sameSeats(last.eligible, eligible)) {
        pots[pots.length - 1] = { ...last, amount: last.amount + amount, cap: level };
      } else {
        pots.push({ index: pots.length, amount, eligible, cap: level });
      }
    }
    prev = level;
  }

  // 重新编号（合并后 index 需连续）
  return pots.map((p, i) => ({ ...p, index: i }));
}

function sameSeats(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * 构造零头分配的顺时针优先顺序：从庄家按钮**左侧**第一个座位开始（PRD 1.2）。
 * @param buttonSeat 按钮所在座位
 * @param seatCount  桌位总数
 */
export function oddChipOrder(buttonSeat: number, seatCount: number): number[] {
  const order: number[] = [];
  for (let i = 1; i <= seatCount; i++) order.push((buttonSeat + i) % seatCount);
  return order;
}

/**
 * 结算全部池。
 *
 * @param contributions 本手每个座位的投入与弃牌状态
 * @param handValue     座位 → 7 张牌力值（数值越大越强）。仅未弃牌且参与比牌的座位需要提供。
 *                      若某座位缺失牌力值（如已弃牌），则不会成为任何池的赢家。
 * @param buttonSeat    按钮座位，用于零头顺时针分配
 * @param seatCount     桌位总数
 */
export function settlePots(
  contributions: readonly PotContribution[],
  handValue: ReadonlyMap<number, number>,
  buttonSeat: number,
  seatCount: number,
): Settlement {
  const pots = buildPots(contributions);
  const order = oddChipOrder(buttonSeat, seatCount);
  const payouts = new Map<number, number>();
  const awards: PotAward[] = [];

  for (const pot of pots) {
    // 该池中实际能比牌的座位（必须有牌力值）
    const contenders = pot.eligible.filter((s) => handValue.has(s));

    if (contenders.length === 0) {
      // 理论上不可达：每个池至少有 1 名未弃牌玩家。留作显式防御，避免筹码凭空消失。
      throw new Error(`池 ${pot.index} 没有任何可比牌的玩家，无法结算（金额 ${pot.amount}）`);
    }

    let best = -Infinity;
    for (const s of contenders) {
      const v = handValue.get(s) as number;
      if (v > best) best = v;
    }
    const winners = contenders.filter((s) => handValue.get(s) === best).sort((a, b) => a - b);

    const share = Math.floor(pot.amount / winners.length);
    const remainder = pot.amount - share * winners.length;

    // 零头：按顺时针顺序（按钮左侧起）在赢家中依次派发 1 个筹码
    const winnerSet = new Set(winners);
    const clockwiseWinners = order.filter((s) => winnerSet.has(s));
    const remainderSeats = clockwiseWinners.slice(0, remainder);

    const potPayouts = new Map<number, number>();
    for (const s of winners) potPayouts.set(s, share);
    for (const s of remainderSeats) potPayouts.set(s, (potPayouts.get(s) as number) + 1);

    for (const [s, v] of potPayouts) payouts.set(s, (payouts.get(s) ?? 0) + v);

    awards.push({
      potIndex: pot.index,
      amount: pot.amount,
      winners,
      payouts: potPayouts,
      remainder,
      remainderSeats,
    });
  }

  const totalContributed = contributions.reduce((s, c) => s + c.contributed, 0);
  let totalPaid = 0;
  for (const v of payouts.values()) totalPaid += v;

  // AC1.3 硬断言：结算总额必须恒等于投入总额，差额必须为 0
  if (totalPaid !== totalContributed) {
    throw new Error(`筹码不守恒：投入 ${totalContributed}，派发 ${totalPaid}`);
  }

  return { pots, awards, payouts, totalContributed, totalPaid };
}

/**
 * 无需比牌的结算：只剩 1 名未弃牌玩家时，全部池归其所有（PRD 六章「全部弃牌则提前结束」）。
 * 注意仍走 settlePots 以复用守恒断言。
 */
export function settleUncontested(
  contributions: readonly PotContribution[],
  winnerSeat: number,
  buttonSeat: number,
  seatCount: number,
): Settlement {
  const alive = contributions.filter((c) => !c.folded);
  if (alive.length !== 1 || alive[0].seat !== winnerSeat) {
    throw new Error(`settleUncontested 要求恰好 1 名未弃牌玩家且为座位 ${winnerSeat}`);
  }
  return settlePots(contributions, new Map([[winnerSeat, 0]]), buttonSeat, seatCount);
}
